import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import update from 'react-addons-update'
import { FormattedMessage, injectIntl } from 'react-intl'
import PhoneInput from 'react-phone-number-input'
import { parsePhoneNumberFromString } from 'libphonenumber-js'

import {
  SelectInput,
  EmailInput,
  isEmail,
} from './Inputs'

import RedX from '../Graphics/RedX'

import AppDispatcher from '../../../dispatchers/AppDispatcher'

import 'react-phone-number-input/style.css'
import styles from '../../../../css/settings.module.css'

const validateAddresses = (addresses, method) => {
  let answer = false
  if (method === 'email') {
    for (let i = 0; i < addresses.length; i += 1) {
      if (!isEmail(addresses[i])) return false
      answer = true
    }
  } else {
    for (let i = 0; i < addresses.length; i += 1) {
      if (typeof addresses[i] !== 'string' || addresses[i].length < 1) return false
      const parsedNumber = parsePhoneNumberFromString(addresses[i])
      if (parsedNumber === undefined || !parsedNumber.isPossible()) return false
      answer = true
    }
  }
  return answer
}

const InputContactInfo = ({ method: initialMethod, addresses: initialAddresses, callback, isEmailOnly, intl: { formatMessage } }) => {
  const [method, setMethod] = useState(initialMethod)
  const [addresses, setAddresses] = useState(initialAddresses)
  const [testAddresses, setTestAddresses] = useState(initialAddresses)
  const [invalid, setInvalid] = useState(!validateAddresses(initialAddresses, initialMethod))

  const updateNewRuleContactInfo = () => {
    AppDispatcher.dispatch({
      action: 'update-new-rule-contact-info',
      addresses,
      method,
      invalid,
    })
  }

  useEffect(() => {
    updateNewRuleContactInfo()
  }, [addresses, method, invalid])

  const childChange = (event, callbackFn) => {
    const { target: { name, value } } = event
    let valid = false
    let newAddresses = addresses
    let newTestAddresses = testAddresses

    if (name === 'method') {
      valid = validateAddresses(testAddresses, value)

      // addresses are cleared only when switching between email and sms/call
      const requiresPhone = /^sms|call$/
      const valueIsPhoneType = requiresPhone.test(value)
      const methodIsPhoneType = requiresPhone.test(method)

      if (valueIsPhoneType !== methodIsPhoneType) {
        newAddresses = ['']
        newTestAddresses = ['']
        valid = false
      }
    }

    setMethod(value)
    setAddresses(newAddresses)
    setTestAddresses(newTestAddresses)
    setInvalid(!valid)

    if (callbackFn) callbackFn(newAddresses)
  }

  const handleChangePhone = (number, item, callbackFn) => {
    const newTestAddresses = update(testAddresses, {
      $splice: [[item, 1, number]],
    })

    const valid = validateAddresses(newTestAddresses, method)

    if (valid) {
      const newAddresses = update(addresses, {
        $splice: [[item, 1, number]],
      })
      setAddresses(newAddresses)
      setTestAddresses(newTestAddresses)
      setInvalid(!valid)
      if (callbackFn) callbackFn(newAddresses)
    } else {
      setTestAddresses(newTestAddresses)
      setInvalid(!valid)
      if (callbackFn) callbackFn(addresses)
    }
  }

  const handleChangeEmail = (e, item, callbackFn) => {
    const number = e.target.value
    const newTestAddresses = update(testAddresses, {
      $splice: [[item, 1, number]],
    })

    const valid = validateAddresses(newTestAddresses, method)

    if (valid) {
      const newAddresses = update(addresses, {
        $splice: [[item, 1, number]],
      })
      setAddresses(newAddresses)
      setTestAddresses(newTestAddresses)
      setInvalid(!valid)
      if (callbackFn) callbackFn(newAddresses)
    } else {
      const newAddresses = update(addresses, {
        $splice: [[item, 1, number]],
      })
      setAddresses(newAddresses)
      setTestAddresses(newTestAddresses)
      setInvalid(!valid)
      if (callbackFn) callbackFn(newAddresses)
    }
  }

  const addAddress = () => {
    const newAddresses = update(addresses, {
      $push: [''],
    })
    setAddresses(newAddresses)
    setInvalid(true)
    if (callback) callback(newAddresses)
  }

  const removeAddress = (item) => {
    const newAddresses = update(addresses, {
      $splice: [[item, 1]],
    })
    const newTestAddresses = update(testAddresses, {
      $splice: [[item, 1]],
    })
    setAddresses(newAddresses)
    setTestAddresses(newTestAddresses)
    if (callback) callback(newAddresses)
  }

  const addressesGUI = []

  for (let i = 0; i < addresses.length; i += 1) {
    let newAddress
    let deleteAddress
    if (i === addresses.length - 1 &&
       ((method !== 'email' && validateAddresses([addresses[i]])) ||
        validateAddresses([addresses[i]], 'email'))) {
      newAddress = (
        <button
          type='button'
          className={styles.arrayButton}
          onClick={addAddress}
        >
          <i
            aria-hidden='true'
            className='fa fa-plus-circle'
            style={{ paddingLeft: '5px' }}
            title={formatMessage({
              id: 'rules.contact.info.new.address',
              defaultMessage: 'New Address'
            })}
          />
        </button>
      )
    }

    if (addresses.length > 1) {
      deleteAddress = (
        <button
          type='button'
          className={styles.arrayButton}
          onClick={() => { removeAddress(i) }}
        >
          <i
            aria-hidden='true'
            className='fa fa-minus-circle'
            style={{ paddingLeft: '5px' }}
            title={formatMessage({
              id: 'general.delete',
              defaultMessage: 'delete'
            })}
          />
        </button>
      )
    }

    if (method === 'email') {
      addressesGUI.push(
        <div className={styles.array} key={`${method}-${i}`}>
          <div className={styles.ruleDecimalWithControls}>
            <EmailInput
              value={testAddresses[i]}
              childChange={(e) => {
                handleChangeEmail(e, i, callback)
              }}
              style={styles.ruleDecimalInput}
            />
          </div>
          {!validateAddresses([testAddresses[i]], 'email') && (
            <div
              aria-hidden='true'
              className={styles.arrayButton}
              style={{ paddingLeft: '5px', paddingRight: '5px', alignSelf: 'center' }}
            >
              <RedX style={{ height: '24px', width: '24px' }} />
            </div>
          )}
          {deleteAddress}
          {newAddress}
        </div>
      )
    } else if (!isEmailOnly) {
      addressesGUI.push(
        <div className={styles.array} key={`${method}-${i}`}>
          <div className={styles.ruleDecimalWithControls} style={{ width: 'auto' }}>
            <PhoneInput
              value={testAddresses[i]}
              onChange={(phone) => {
                handleChangePhone(phone, i, callback)
              }}
              country='US'
              countries={['US', 'CA', 'CL']}
              displayInitialValueAsLocalNumber
            />
          </div>
          {!validateAddresses([testAddresses[i]]) && (
            <div
              aria-hidden='true'
              className={styles.arrayButton}
              style={{ paddingLeft: '5px', paddingRight: '5px', position: 'relative', bottom: '-2px' }}
            >
              <RedX style={{ height: '24px', width: '24px' }} />
            </div>
          )}
          {deleteAddress}
          {newAddress}
        </div>
      )
    }
  }

  let addressesTitle

  if (method === 'email') {
    addressesTitle = formatMessage({
      id: 'rules.contact.info.email',
      defaultMessage: 'Email Address(es)',
      description: 'Email Address(es) heading',
    })
  } else {
    addressesTitle = formatMessage({
      id: 'rules.contact.info.phone',
      defaultMessage: 'Phone Number(s)',
      description: 'Phone Number(s) heading',
    })
  }

  return (
    <div>
      {!isEmailOnly && (
        <section>
          <FormattedMessage
            id='rules.contact.method.name'
            defaultMessage='Method'
            description='Method heading'
            tagName='h5'
          />
          <div className={styles.ruleSelect}>
            <SelectInput
              value={method}
              field='method'
              childChange={childChange}
              options={[
                {
                  value: 'sms',
                  name: formatMessage({
                    id: 'rules.contact.method.sms',
                    defaultMessage: 'SMS Text',
                    description: 'SMS Text option',
                  }),
                },
                {
                  value: 'email',
                  name: formatMessage({
                    id: 'rules.contact.method.email',
                    defaultMessage: 'Email',
                    description: 'Email option',
                  }),
                },
                {
                  value: 'call',
                  name: formatMessage({
                    id: 'rules.contact.method.call',
                    defaultMessage: 'Call',
                    description: 'Call option',
                  }),
                },
              ]}
              style={styles.ruleSelectInput}
            />
          </div>
        </section>
      )}
      <section>
        <h5>
          {addressesTitle}
        </h5>
        {addressesGUI}
      </section>
    </div>
  )
}

InputContactInfo.propTypes = {
  isEmailOnly: PropTypes.bool,
  method: PropTypes.string,
  addresses: PropTypes.array,
  callback: PropTypes.func,
}

export default injectIntl(InputContactInfo)
